import React from 'react';
import Loader from './Loader';

const PROPERTY_OPTIONS = [
  { value: 'repairDate', label: 'Repair Date' },
  { value: 'completedDate', label: 'Completion Date' },
  { value: 'repairReason', label: 'Repair Reason' },
  { value: 'status', label: 'Status' },
  { value: 'repairCost', label: 'Repair Cost' },
  { value: 'repairVendor', label: 'Repair Vendor' },
  { value: 'billNumber', label: 'Bill Number' },
  { value: 'notes', label: 'Notes' },
  { value: 'repairSource', label: 'Repair Initiated By' }
];

const STATUS_PROPERTY_RULES = {
  Pending: ['repairDate', 'repairReason', 'status', 'repairCost', 'repairVendor', 'billNumber', 'notes', 'repairSource'],
  InRepair: ['repairDate', 'repairReason', 'status', 'repairCost', 'repairVendor', 'billNumber', 'notes', 'repairSource'],
  Completed: ['completedDate', 'repairCost', 'repairVendor', 'billNumber', 'notes'],
  Dead: ['completedDate', 'repairCost', 'repairVendor', 'billNumber', 'notes']
};

const rowSurfaceStyle = {
  backgroundColor: 'rgba(17, 24, 39, 0.55)',
  borderColor: 'rgba(255, 193, 7, 0.22)'
};

function RepairBatchEditModal({
  show,
  saving,
  batchEditData,
  setBatchEditData,
  onClose,
  onSubmit
}) {
  const selectedStatuses = [...new Set(
    (batchEditData.selectedRepairs || [])
      .map((repair) => repair?.status)
      .filter(Boolean)
  )];

  const allowedProperties = PROPERTY_OPTIONS.filter((option) => (
    selectedStatuses.length === 0
      ? true
      : selectedStatuses.every((status) => (STATUS_PROPERTY_RULES[status] || ['notes']).includes(option.value))
  ));

  const allowedPropertyKeys = new Set(allowedProperties.map((option) => option.value));

  const updateSelectedProperty = (index, nextPropertyKey) => {
    if (!allowedPropertyKeys.has(nextPropertyKey)) {
      return;
    }

    const updatedProperties = [...batchEditData.selectedProperties];
    updatedProperties[index] = nextPropertyKey;

    setBatchEditData({
      ...batchEditData,
      selectedProperties: updatedProperties
    });
  };

  const getAvailableOptions = (currentPropertyKey) => PROPERTY_OPTIONS.filter((option) => (
    allowedPropertyKeys.has(option.value)
    && (option.value === currentPropertyKey || !batchEditData.selectedProperties.includes(option.value))
  ));

  const addSelectedProperty = () => {
    if (!batchEditData.propertyToAdd
      || !allowedPropertyKeys.has(batchEditData.propertyToAdd)
      || batchEditData.selectedProperties.includes(batchEditData.propertyToAdd)) {
      return;
    }

    setBatchEditData({
      ...batchEditData,
      selectedProperties: [...batchEditData.selectedProperties, batchEditData.propertyToAdd],
      propertyToAdd: ''
    });
  };

  const removeSelectedProperty = (propertyKey) => {
    setBatchEditData({
      ...batchEditData,
      selectedProperties: batchEditData.selectedProperties.filter((property) => property !== propertyKey)
    });
  };

  const renderPropertyEditor = (propertyKey, compact = false) => {
    const wrapperProps = compact ? { className: 'm-0' } : {};
    const labelClassName = compact ? 'form-label small text-uppercase text-muted mb-2' : 'form-label';

    switch (propertyKey) {
      case 'repairDate':
        return (
          <div key={propertyKey} {...wrapperProps}>
            <label className={labelClassName}>Repair Date</label>
            <input
              type="date"
              className="form-control"
              value={batchEditData.repairDate}
              onChange={(e) => setBatchEditData({ ...batchEditData, repairDate: e.target.value })}
              required
            />
          </div>
        );
      case 'completedDate':
        return (
          <div key={propertyKey} {...wrapperProps}>
            <label className={labelClassName}>Completion Date</label>
            <input
              type="date"
              className="form-control"
              value={batchEditData.completedDate}
              onChange={(e) => setBatchEditData({ ...batchEditData, completedDate: e.target.value })}
            />
          </div>
        );
      case 'repairReason':
        return (
          <div key={propertyKey} {...wrapperProps}>
            <label className={labelClassName}>Repair Reason</label>
            <textarea
              className="form-control"
              rows="2"
              value={batchEditData.repairReason}
              onChange={(e) => setBatchEditData({ ...batchEditData, repairReason: e.target.value })}
              required
            />
          </div>
        );
      case 'status':
        return (
          <div key={propertyKey} {...wrapperProps}>
            <label className={labelClassName}>Status</label>
            <select
              className="form-select"
              value={batchEditData.status}
              onChange={(e) => setBatchEditData({ ...batchEditData, status: e.target.value })}
            >
              <option value="Pending">Pending</option>
              <option value="InRepair">In Repair</option>
              <option value="Completed">Completed</option>
              <option value="Dead">Dead</option>
            </select>
          </div>
        );
      case 'repairCost':
        return (
          <div key={propertyKey} {...wrapperProps}>
            <label className={labelClassName}>Repair Cost</label>
            <input
              type="number"
              className="form-control"
              step="0.01"
              value={batchEditData.repairCost}
              onChange={(e) => setBatchEditData({ ...batchEditData, repairCost: e.target.value })}
            />
          </div>
        );
      case 'repairVendor':
        return (
          <div key={propertyKey} {...wrapperProps}>
            <label className={labelClassName}>Repair Vendor</label>
            <input
              type="text"
              className="form-control"
              value={batchEditData.repairVendor}
              onChange={(e) => setBatchEditData({ ...batchEditData, repairVendor: e.target.value })}
            />
          </div>
        );
      case 'billNumber':
        return (
          <div key={propertyKey} {...wrapperProps}>
            <label className={labelClassName}>Bill Number</label>
            <input
              type="text"
              className="form-control"
              value={batchEditData.billNumber}
              onChange={(e) => setBatchEditData({ ...batchEditData, billNumber: e.target.value })}
            />
          </div>
        );
      case 'notes':
        return (
          <div key={propertyKey} {...wrapperProps}>
            <label className={labelClassName}>Notes</label>
            <textarea
              className="form-control"
              rows="2"
              value={batchEditData.notes}
              onChange={(e) => setBatchEditData({ ...batchEditData, notes: e.target.value })}
            />
          </div>
        );
      case 'repairSource':
        return (
          <div key={propertyKey} {...wrapperProps}>
            <label className={labelClassName}>Repair Initiated By</label>
            <select
              className="form-select"
              value={batchEditData.repairSource}
              onChange={(e) => setBatchEditData({ ...batchEditData, repairSource: parseInt(e.target.value, 10) })}
            >
              <option value={0}>Office</option>
              <option value={1}>Employee Reported</option>
              <option value={2}>Employee Returned</option>
            </select>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className={`modal fade ${show ? 'show d-block' : ''}`} style={{ backgroundColor: show ? 'rgba(0,0,0,0.5)' : 'transparent' }}>
      <div className="modal-dialog modal-lg">
        <div className="modal-content border-0 shadow-lg">
          <div className="modal-header bg-warning text-dark">
            <h5 className="modal-title">
              <i className="fas fa-layer-group me-2"></i>Batch Edit Repairs
            </h5>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>
          <form onSubmit={onSubmit}>
            <div className="modal-body p-4">
              <div className="alert alert-warning mb-4">
                {batchEditData.selectedRepairs.length} selected repair(s) will be updated together.
              </div>

              <div className="vstack gap-4">
                <div>
                  <label className="form-label">Add Property</label>
                  {selectedStatuses.length > 0 && (
                    <div className="text-muted small mb-2">
                      Available fields depend on the common current status of the selected repairs: {selectedStatuses.join(', ')}.
                    </div>
                  )}
                  <div className="border rounded-3 p-3" style={rowSurfaceStyle}>
                    <div className="row g-3 align-items-start">
                      <div className="col-lg-4">
                        <label className="form-label small text-uppercase text-muted mb-2">Property</label>
                        <select
                          className="form-select"
                          data-testid="repair-bulk-property-select"
                          value={batchEditData.propertyToAdd}
                          onChange={(e) => setBatchEditData({ ...batchEditData, propertyToAdd: e.target.value })}
                        >
                          <option value="">Select property to update</option>
                          {allowedProperties
                            .filter((option) => !batchEditData.selectedProperties.includes(option.value))
                            .map((option) => (
                              <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                        </select>
                      </div>
                      <div className="col-lg-6">
                        {batchEditData.propertyToAdd ? (
                          renderPropertyEditor(batchEditData.propertyToAdd, true)
                        ) : (
                          <div className="text-muted small pt-4">
                            Select a property and enter the new value here.
                          </div>
                        )}
                      </div>
                      <div className="col-lg-2 d-flex align-items-start justify-content-lg-end pt-lg-4">
                        <button
                          type="button"
                          className="btn btn-warning w-100"
                          onClick={addSelectedProperty}
                          disabled={!batchEditData.propertyToAdd}
                        >
                          Add
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {batchEditData.selectedProperties.length > 0 ? (
                  <div className="vstack gap-3">
                    {batchEditData.selectedProperties.map((propertyKey, index) => (
                      <div key={`${propertyKey}-${index}`} className="border rounded-3 p-3" style={rowSurfaceStyle}>
                        <div className="row g-3 align-items-start">
                          <div className="col-lg-4">
                            <label className="form-label small text-uppercase text-muted mb-2">Property</label>
                            <select
                              className="form-select"
                              value={propertyKey}
                              onChange={(e) => updateSelectedProperty(index, e.target.value)}
                            >
                              {getAvailableOptions(propertyKey).map((option) => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                              ))}
                            </select>
                          </div>
                          <div className="col-lg-6">
                            {renderPropertyEditor(propertyKey, true)}
                          </div>
                          <div className="col-lg-2 d-flex align-items-start justify-content-lg-end pt-lg-4">
                            <button
                              type="button"
                              className="btn btn-outline-danger"
                              onClick={() => removeSelectedProperty(propertyKey)}
                              aria-label={`Remove ${PROPERTY_OPTIONS.find((option) => option.value === propertyKey)?.label || propertyKey}`}
                            >
                              <i className="fas fa-times"></i>
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-muted small">
                    Add one property at a time. Each added row will be included in the bulk update.
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer bg-light border-0 p-4">
              <button type="button" className="btn btn-outline-secondary" onClick={onClose}>
                Cancel
              </button>
              <button
                type="submit"
                className={`btn btn-warning ${saving ? 'btn-loading' : ''}`}
                disabled={saving || batchEditData.selectedRepairs.length === 0 || batchEditData.selectedProperties.length === 0}
              >
                {saving ? <Loader size="small" /> : 'Update Selected'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default RepairBatchEditModal;
